import { useState, useRef, useEffect } from "react";
import { DocFieldState } from "../types";
import { RejectionReason } from "../../redux/onBoardingSlice.types";
import { resolveReason } from "../utils";

export interface UseDocFieldStateProps {
  externalState?: DocFieldState;
  onView?: () => void;
  onStartRejection?: () => void;
  onSelectReason?: (r: string) => void;
  onCommentChange?: (v: string) => void;
  onClearAll?: () => void;
  onCancelRejection?: () => void;
  onConfirmRejection?: () => void;
  rejectionReasons?: RejectionReason[];
}

export const INIT_DOC_FIELD_STATE: DocFieldState = {
  state: "collapsed",
  viewed: false,
  reasons: [],
  pendingReason: "",
  pendingComment: "",
};

/**
 * Encapsulates all state and handler logic for a DocumentField.
 *
 * Supports two modes:
 * - **Uncontrolled**: no `externalState` — manages its own internal state.
 * - **Controlled**: `externalState` is provided — all mutations are delegated
 *   to the supplied external handlers.
 *
 * Keeping state logic here makes DocumentField a pure renderer and lets this
 * hook be tested in isolation with `renderHook`.
 */
export const useDocFieldState = ({
  externalState,
  onView: extOnView,
  onStartRejection: extOnStartRejection,
  onSelectReason: extOnSelect,
  onCommentChange: extOnCommentChange,
  onClearAll: extOnClear,
  onCancelRejection: extOnCancel,
  onConfirmRejection: extOnConfirm,
  rejectionReasons = [],
}: UseDocFieldStateProps) => {
  const [internalState, setInternalState] = useState<DocFieldState>(() => ({ ...INIT_DOC_FIELD_STATE }));

  const isControlled = externalState !== undefined;
  const state = externalState ?? internalState;
  const isOpen = state.state === "preview" || state.state === "rejecting";

  const rejectPanelRef = useRef<HTMLDivElement>(null);
  const prevStateRef = useRef(state.state);

  useEffect(() => {
    const prev = prevStateRef.current;
    prevStateRef.current = state.state;
    if (prev !== "rejecting" && state.state === "rejecting") {
      setTimeout(() => rejectPanelRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
    }
  }, [state.state]);

  const update = (patch: Partial<DocFieldState>) => {
    if (!isControlled) setInternalState((prev) => ({ ...prev, ...patch }));
  };

  const handleView =
    extOnView ??
    (() => update({ state: isOpen ? "collapsed" : "preview", viewed: true, pendingReason: "", pendingComment: "" }));

  const handleStartRejection = extOnStartRejection ?? (() => update({ state: "rejecting" }));

  const handleSelect = extOnSelect ?? ((r: string) => update({ pendingReason: r, pendingComment: "" }));

  const handleCommentChange = extOnCommentChange ?? ((v: string) => update({ pendingComment: v }));

  const handleClear = extOnClear ?? (() => update({ pendingReason: "", pendingComment: "" }));

  const handleCancel = extOnCancel ?? (() => update({ state: "preview", pendingReason: "", pendingComment: "" }));

  const handleConfirm =
    extOnConfirm ??
    (() => {
      const reason = resolveReason(state.pendingReason, state.pendingComment, rejectionReasons);
      update({ state: "rejected", reasons: reason ? [reason] : [], pendingReason: "", pendingComment: "" });
    });

  return {
    state,
    isOpen,
    rejectPanelRef,
    handleView,
    handleStartRejection,
    handleSelect,
    handleCommentChange,
    handleClear,
    handleCancel,
    handleConfirm,
  };
};
